import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { fetchTenant, fetchTenantGeneralInfo, startMigration, startTestMigration, updateTenantDataVerified, updateTenantFeaturesAdjusted } from "../app/actions";
import { Button, Form } from "react-bootstrap"
import getGadgetMigrationStatus from "../api/getGadgetMigrationStatus";
import getGadgetTestMigrationStatus from "../api/getGadgetTestMigrationStatus";
import postTenantProjectStatusCleanup from "../api/postTenantProjectStatusCleanup";

const TenantMigration = () => {
    const tenant = useSelector(state => state.app.tenant)
    const token = useSelector(state => state.app.token)
    const tenantGeneralInfo = useSelector(state => state.app.tenantGeneralInfo)
    const fetchingTenantGeneralInfo = useSelector(state => state.app.fetchingTenantGeneralInfo)
    const dispatch = useDispatch()
    const [migrating, setMigrating] = useState(false);
    const [testMigrating, setTestMigrating] = useState(false);
    const [tenantName, setTenantName] = useState('');
    const [projectStatusCutoffDate, setProjectStatusCutoffDate] = useState(() =>
        moment().subtract(12, "months").format("YYYY-MM-DD")
    );
    const [cleaningProjectStatuses, setCleaningProjectStatuses] = useState(false);
    const [projectStatusCleanupResult, setProjectStatusCleanupResult] = useState(null);

    const environment = process.env.REACT_APP_ENVIRONMENT;

    const checkMigrationStatus = async () => {
 
        if(! tenant || !token || !migrating) {
            return;
        }
        console.log('fetching migration status');
        try {
            const response = await getGadgetMigrationStatus(token, tenant.id);

            if(response.success) {

                const status = response.data.data.status;
                if(status !== tenant.MigrationStatus) {
                    console.log('migration status diff, updating tenant');
                    dispatch(fetchTenant(tenant.id));
                }
            }
    
        } catch (error) {
            console.error('Error fetching migration status:', error);
        }
    };

    const checkTestMigrationStatus = async () => {
 
        if(! tenant || !token || !testMigrating) {
            return;
        }
        console.log('fetching test migration status');
        try {
            const response = await getGadgetTestMigrationStatus(token, tenant.id);

            if(response.success) {

                const status = response.data.data.status;
                if(status !== tenant.MigrationTestStatus) {
                    console.log('migration test status diff, updating tenant');
                    dispatch(fetchTenant(tenant.id));
                }
            }
    
        } catch (error) {
            console.error('Error fetching test migration status:', error);
        }
    };

    useEffect(() => {
        const intervalId = setInterval(checkMigrationStatus, 10000); // Poll every 10 seconds
        const intervalIdTest = setInterval(checkTestMigrationStatus, 10000); // Poll every 10 seconds

        // Cleanup function to clear the interval when component unmounts or expected data is received
        return () => {
            clearInterval(intervalId);
            clearInterval(intervalIdTest);
        };
    }, []);

    useEffect(() => {
        if (tenant?.id) {
            dispatch(fetchTenantGeneralInfo(tenant.id))
        }
    }, [tenant?.id, dispatch])

    const runProjectStatusCleanup = async () => {
        if (!tenant?.id || !token || cleaningProjectStatuses) return;

        const ok = window.confirm(
            `This will update project statuses for projects created before ${projectStatusCutoffDate}.\n\n` +
            `- Projects with NO reports will be marked as archived (sets done)\n` +
            `- Projects with reports, but NO reports since ${projectStatusCutoffDate}, will be marked as completed (sets employee_done_marker)\n` +
            `- Projects with any report on/after ${projectStatusCutoffDate} will not be affected\n\n` +
            `Continue?`
        );
        if (!ok) return;

        setCleaningProjectStatuses(true);
        setProjectStatusCleanupResult(null);

        try {
            const res = await postTenantProjectStatusCleanup(token, tenant.id, {
                cutoff_date: projectStatusCutoffDate,
            });
            setProjectStatusCleanupResult(res);
            dispatch(fetchTenantGeneralInfo(tenant.id));
        } finally {
            setCleaningProjectStatuses(false);
        }
    };

    const handleTenantMigration = () => {

        const { id, FullName } = tenant;

        if(window.confirm(`Vill du starta migration av ${FullName} data? Kunden kommer inte längre kunna logga in på MyGizmo 2.0`)) {
            
            setMigrating(true);

            dispatch(startMigration(id));
        }
    };

    const handleTenantTestMigration = () => {

        const { id, FullName } = tenant;

        if(window.confirm(`Vill du starta TEST migration av ${FullName} data?`)) {

            setTestMigrating(true);

            dispatch(startTestMigration(id));
        }
    };


    if(!tenant) {
        return <div>loading</div>
    }

    const saveFeaturesAdjusted = (FeaturesAdjusted) =>
        dispatch(updateTenantFeaturesAdjusted(tenant.id, FeaturesAdjusted)).then(() =>
            dispatch(fetchTenant(tenant.id))
        );

    const saveDataVerified = (DataVerified) =>
        dispatch(updateTenantDataVerified(tenant.id, DataVerified)).then(() =>
            dispatch(fetchTenant(tenant.id))
        );

    return <div style={{display: "flex", flexDirection: "column", gap: "20px"}}>
        <h5>General information</h5>
        <div style={{
            display: "grid",
            gridTemplateColumns: "max-content max-content max-content minmax(0, 1fr)",
            columnGap: "0.5em",
            rowGap: "0.75em",
            alignItems: "baseline",
            border: "1px solid #ccc",
            padding: "1em",
            borderRadius: "1em",
            marginBottom: "2em"
        }}>
            <span>Projects with fixed price:</span>
            <span style={{ fontWeight: "bold" }}>
                {fetchingTenantGeneralInfo
                    ? "…"
                    : tenantGeneralInfo != null
                      ? tenantGeneralInfo.projects_with_fixed_price_count
                      : "—"}
            </span>
            <span>Last created:</span>
            <span style={{ fontWeight: "bold" }}>
                {fetchingTenantGeneralInfo
                    ? "…"
                    : tenantGeneralInfo != null && tenantGeneralInfo.last_fixed_price_project_created_at
                      ? moment(tenantGeneralInfo.last_fixed_price_project_created_at).format("LL")
                      : "—"}
            </span>
            <p style={{
                gridColumn: "1 / -1",
                margin: 0,
                marginTop: "0.5em",
                paddingTop: "0.75em",
                borderTop: "1px solid #e0e0e0",
                fontSize: "0.875rem",
                color: "#555",
                lineHeight: 1.5,
                maxWidth: "100%",
            }}>
                Unhandled invoice details for fixed price projects in MG-3 are based on the reports, but in MG-4 it will be based on projects actual fixed price.
            </p>

            <div style={{
                gridColumn: "1 / -1",
                width: "100%",
                minWidth: 0,
                marginTop: "0.75em",
                paddingTop: "0.75em",
                borderTop: "1px solid #e0e0e0",
            }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "max-content max-content max-content minmax(0, 1fr)",
                    columnGap: "0.5em",
                    rowGap: "0.75em",
                    alignItems: "baseline",
                    marginBottom: "1em",
                }}>
                    <span>Active projects:</span>
                    <span style={{ fontWeight: "bold" }}>
                        {fetchingTenantGeneralInfo
                            ? "…"
                            : tenantGeneralInfo != null
                              ? tenantGeneralInfo.active_projects_count
                              : "—"}
                    </span>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "max-content minmax(180px, 220px) max-content",
                        alignItems: "center",
                        columnGap: "0.75em",
                        rowGap: "0.5em",
                        paddingLeft: "2em",
                    }}>
                        <span style={{ whiteSpace: "nowrap" }}>Cutoff date:</span>
                        <Form.Control
                            type="date"
                            value={projectStatusCutoffDate}
                            onChange={(e) => setProjectStatusCutoffDate(e.target.value)}
                        />
                    
                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={cleaningProjectStatuses || !projectStatusCutoffDate}
                            onClick={runProjectStatusCleanup}
                        >
                            {cleaningProjectStatuses ? "Updating…" : "Update statuses"}
                        </Button>
                    </div>
                </div>
                {projectStatusCleanupResult != null && (
                    <p style={{
                        margin: 0,
                        marginTop: "0.5em",
                        fontSize: "0.875rem",
                        color: "#555",
                        lineHeight: 1.5,
                    }}>
                        Marked done (no reports): <b>{projectStatusCleanupResult.marked_done_count ?? "—"}</b>.{" "}
                        Marked completed (old reports):{" "}
                        <b>{projectStatusCleanupResult.marked_employee_done_marker_count ?? "—"}</b>.
                    </p>
                )}
                <p style={{
                    margin: 0,
                    marginTop: "0.5em",
                    paddingTop: "0.75em",
                    borderTop: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    color: "#555",
                    lineHeight: 1.5,
                }}>
                    If active projects is more than 100, mark unused projects as completed or inactive.
                </p>
            </div>
            <div style={{
                gridColumn: "1 / -1",
                width: "100%",
                minWidth: 0,
                marginTop: "0.75em",
                paddingTop: "0.75em",
                borderTop: "1px solid #e0e0e0",
            }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "max-content max-content max-content minmax(0, 1fr)",
                    columnGap: "0.5em",
                    rowGap: "0.75em",
                    alignItems: "baseline",
                    marginBottom: "1em",
                }}>
                    <span>Credit Supplier invoices:</span>
                    <span style={{ fontWeight: "bold" }}>
                        {fetchingTenantGeneralInfo
                            ? "…"
                            : tenantGeneralInfo != null
                              ? tenantGeneralInfo.supplier_invoices_negative_total_count
                              : "—"}
                    </span>
                    <span>Last created:</span>
                    <span style={{ fontWeight: "bold" }}>
                        {fetchingTenantGeneralInfo
                            ? "…"
                            : tenantGeneralInfo != null && tenantGeneralInfo.last_supplier_invoice_negative_total_created_at
                              ? moment(tenantGeneralInfo.last_supplier_invoice_negative_total_created_at).format("LL")
                              : "—"}
                    </span>
                    <span>Credit distributions:</span>
                    <span style={{ fontWeight: "bold" }}>
                        {fetchingTenantGeneralInfo
                            ? "…"
                            : tenantGeneralInfo != null
                              ? tenantGeneralInfo.supplier_invoice_relations_negative_amount_count
                              : "—"}
                    </span>
                    <span>Last created:</span>
                    <span style={{ fontWeight: "bold" }}>
                        {fetchingTenantGeneralInfo
                            ? "…"
                            : tenantGeneralInfo != null && tenantGeneralInfo.last_supplier_invoice_relation_negative_amount_created_at
                              ? moment(tenantGeneralInfo.last_supplier_invoice_relation_negative_amount_created_at).format("LL")
                              : "—"}
                    </span>
                </div>
                <p style={{
                    margin: 0,
                    marginTop: "0.5em",
                    paddingTop: "0.75em",
                    borderTop: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    color: "#555",
                    lineHeight: 1.5,
                }}>
                    Credit supplier invoices is currently not supported in MG-4.
                </p>
            </div>
            {!fetchingTenantGeneralInfo &&
                tenantGeneralInfo != null &&
                tenantGeneralInfo.price_list_discounts_with_task_or_product_type_count > 0 && (
                <div style={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    minWidth: 0,
                    marginTop: "0.75em",
                    paddingTop: "0.75em",
                    borderTop: "1px solid #e0e0e0",
                }}>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "max-content max-content",
                        columnGap: "0.5em",
                        rowGap: "0.75em",
                        alignItems: "baseline",
                        marginBottom: "1em",
                    }}>
                        <span>Price list discounts on specific article type:</span>
                        <span style={{ fontWeight: "bold" }}>
                            {tenantGeneralInfo.price_list_discounts_with_task_or_product_type_count}
                        </span>
                    </div>
                    <p style={{
                        margin: 0,
                        marginTop: "0.5em",
                        paddingTop: "0.75em",
                        borderTop: "1px solid #e0e0e0",
                        fontSize: "0.875rem",
                        color: "#555",
                        lineHeight: 1.5,
                    }}>
                        We do not migrate the article type discounts to MG-4, because MG-4 does not support article type discounts the same way.
                    </p>
                    <p style={{
                        margin: 0,
                        fontSize: "0.875rem",
                        color: "#555",
                        lineHeight: 1.5,
                    }}>
                        In MG-4 you instead set the entire price lists discounts to apply on all articles or a sepecific article type.
                    </p>
                </div>
            )}
        </div>

        <Form.Check
            type="switch"
            id="tenant-features-adjusted"
            label="Features adjusted according to contract"
            checked={Boolean(tenant.FeaturesAdjusted)}
            onChange={(e) => {
                saveFeaturesAdjusted(e.target.checked);
            }}
        />
        
        <Form.Check
            type="switch"
            id="tenant-data-verified"
            label="Test migration data verified"
            checked={Boolean(tenant.DataVerified)}
            onChange={(e) => {
                saveDataVerified(e.target.checked);
            }}
        />

        {environment === "production" && (
            <div style={{ borderBottom: "1px solid #ccc", paddingBottom: "30px"}}>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "300px" }}>
                    <h5>Migration Status: {tenant.MigrationTestStatus}</h5>
                    <Button disabled={! tenant || ['scheduled', 'in_progress'].includes(tenant.MigrationTestStatus) || testMigrating} onClick={handleTenantTestMigration}>Starta TEST migration</Button>
                </div>
            </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "300px" }}>
            <h5>Migration Status: {tenant.MigrationStatus}</h5>
            <input placeholder="Skriv in kundens fullständiga namn" type="text" value={tenantName} onChange={(e) => setTenantName(e.target.value)} />
            <Button disabled={! tenant || ['scheduled', 'in_progress', 'successful'].includes(tenant.MigrationStatus) || (tenantName !== tenant.FullName) || migrating} onClick={handleTenantMigration}>Starta migration</Button>
        </div>
    </div>
}

export default TenantMigration
